import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { mergeUniqueFiles, sortFilesByBaseName, filterByExt } from './utils';
import { VALID_ADDON_EXT, VALID_IMG_EXT } from './constants';

export default function useAddonDialogState({ open, mode, editing, onSubmit, onClose }) {
  const isEdit = mode === 'edit';

  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const [createdAt, setCreatedAt] = useState(dayjs());
  const [createdAtDirty, setCreatedAtDirty] = useState(false);

  const [addonFiles, setAddonFiles] = useState([]); // File[]
  const [imgsKeep, setImgsKeep] = useState([]); // string[]
  const [imgFiles, setImgFiles] = useState([]); // File[]
  const [downloads, setDownloads] = useState([]); // {title,url,length}[]
  const [tags, setTags] = useState([]); // để sẵn

  const addonFilesRef = useRef(null);
  const imgsRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    setSaving(false);
    setCreatedAtDirty(false);

    if (isEdit && editing) {
      setTitle(editing.title || '');
      setText(editing.text || '');

      const ca = Number(editing.createdAt);
      setCreatedAt(Number.isFinite(ca) && ca > 0 ? dayjs(ca) : dayjs());

      setAddonFiles([]);
      if (addonFilesRef.current) addonFilesRef.current.value = null;

      setImgsKeep(Array.isArray(editing.imgs) ? editing.imgs : []);
      setImgFiles([]);
      if (imgsRef.current) imgsRef.current.value = null;

      const dls = Array.isArray(editing.downloads) ? editing.downloads : [];
      setDownloads(
        dls.map((d) => ({
          ...d,
          title: d?.title || '',
          url: d?.url || '',
          length: typeof d?.length === 'number' ? d.length : Number(d?.length || 0),
        }))
      );

      setTags(Array.isArray(editing.tags) ? editing.tags : []);
    } else {
      setTitle('');
      setText('');
      setCreatedAt(dayjs());
      setAddonFiles([]);
      setImgsKeep([]);
      setImgFiles([]);
      setDownloads([]);
      setTags([]);

      if (addonFilesRef.current) addonFilesRef.current.value = null;
      if (imgsRef.current) imgsRef.current.value = null;
    }
  }, [open, isEdit, editing]);

  const imgPreviews = useMemo(
    () => imgFiles.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [imgFiles]
  );

  useEffect(() => {
    return () => {
      imgPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [imgPreviews]);

  const onAddonFilesChange = (e) => {
    const list = Array.from(e.target.files || []);
    const filtered = filterByExt(list, VALID_ADDON_EXT);
    setAddonFiles((prev) => sortFilesByBaseName(mergeUniqueFiles(prev, filtered)));
  };

  const removeAddonFileAt = (idx) => setAddonFiles((prev) => prev.filter((_, i) => i !== idx));

  const onImgsChange = (e) => {
    const list = Array.from(e.target.files || []);
    const filtered = filterByExt(list, VALID_IMG_EXT);
    setImgFiles((prev) => sortFilesByBaseName(mergeUniqueFiles(prev, filtered)));
  };

  const removeNewImgAt = (idx) => {
    const p = imgPreviews[idx];
    if (p) URL.revokeObjectURL(p.url);
    setImgFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeKeepImgAt = (idx) => setImgsKeep((prev) => prev.filter((_, i) => i !== idx));

  const moveKeepImg = (fromIndex, toIndex) => {
    setImgsKeep((prev) => {
      if (toIndex < 0 || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  };

  const updateDownloadField = (idx, patch) => {
    setDownloads((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };
  const addDownload = () => setDownloads((prev) => [...prev, { title: '', url: '', length: 0 }]);
  const removeDownloadAt = (idx) => setDownloads((prev) => prev.filter((_, i) => i !== idx));

  const onCreatedAtChange = (v) => {
    setCreatedAt(v || dayjs());
    setCreatedAtDirty(true);
  };

  const submit = async () => {
    if (!title.trim() || !text.trim()) {
      alert('Title và Text là bắt buộc');
      return;
    }
    const futureDownloadCount = downloads.filter((d) => String(d?.url || '').trim()).length + addonFiles.length;
    const futureImageCount = imgsKeep.length + imgFiles.length;
    if (futureDownloadCount === 0 || futureImageCount === 0) {
      const missing = [futureDownloadCount === 0 && 'addon file', futureImageCount === 0 && 'ảnh'].filter(Boolean).join(' và ');
      if (!window.confirm(`Addon đang thiếu ${missing}. Addon sẽ được lưu tạm và không hiển thị trên app mobile. Bạn vẫn muốn lưu?`)) return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('text', text.trim());

      const createdAtMs = createdAt?.valueOf?.();
      if (Number.isFinite(createdAtMs) && createdAtMs > 0) {
        // Create: gửi default now; Edit: chỉ gửi nếu user thay đổi
        if (!isEdit) fd.append('createdAt', String(createdAtMs));
        else if (createdAtDirty) fd.append('createdAt', String(createdAtMs));
      }

      if (isEdit) {
        fd.append('imgs_keep', JSON.stringify(imgsKeep || []));
        fd.append('downloads', JSON.stringify(downloads || []));
        fd.append('tags', JSON.stringify(tags || []));
        imgFiles.forEach((f) => fd.append('imgs', f));
        addonFiles.forEach((f) => fd.append('files', f));
      } else {
        addonFiles.forEach((f) => fd.append('files', f));
        imgFiles.forEach((f) => fd.append('imgs', f));
      }

      await onSubmit(fd);
      onClose();
    } catch (e) {
      alert(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return {
    isEdit,
    refs: { addonFilesRef, imgsRef },
    state: {
      title,
      text,
      saving,
      createdAt,
      addonFiles,
      imgsKeep,
      imgFiles,
      imgPreviews,
      downloads,
      tags,
    },
    actions: {
      setTitle,
      setText,
      onCreatedAtChange,
      onAddonFilesChange,
      removeAddonFileAt,
      onImgsChange,
      removeNewImgAt,
      removeKeepImgAt,
      moveKeepImg,
      updateDownloadField,
      addDownload,
      removeDownloadAt,
      setTags,
      submit,
    },
  };
}
